import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CheckCircleOutline, InfoOutlined, ModeStandbyOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";

import { EXPORT_PROJECT } from "../../constants";
import { ProjectTableColumns } from "../../pages/project/constants";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { CardLayout, MTable } from "../v3";
import { AnalyticsService, CRMService } from "../../services";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const ProjectList = ({ showFilterButton = false, companyId = null, totalCount = 0, setTotalCount = () => {} }) => {
  const [summaryProjects, setSummaryProjects] = useState([]);
  const [clients, setClients] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "projects",
    defaults: { filter: "INPROGRESS" },
    filterValues: ["ALL", "TODO", "INPROGRESS", "DONE"],
  });
  const [loading, setLoading] = useState(true);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const hasExportProject = useHasPermission(EXPORT_PROJECT);

  const fetchData = useCallback(
    async (projectFilters = {}) => {
      setLoading(true);
      try {
        const { data: projectSummary } = await AnalyticsService.getCompanyProjectSummary({
          ...projectFilters,
          ...(companyId !== null && { companyId }),
        });
        setSummaryProjects(projectSummary?.rows);
        setTotalCount(projectSummary?.count);

        const clientIds = Array.from(new Set(projectSummary?.rows?.map((item) => item.clientId)));
        if (clientIds.length > 0) {
          const { data: clients } = await CRMService.getClientList({
            ids: clientIds.join(","),
            limit: clientIds.length,
            includeDeleted: true,
          });
          setClients(clients?.rows);
        }
      } catch (error) {
        toast("Failed to fetch project list", { type: "error" });
        console.error("🚀 ~ fetchData ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount, companyId],
  );

  useEffect(() => {
    fetchData({
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      ...(selectedFilter !== "ALL" && { projectStatus: selectedFilter }),
      projectName: searchText,
    });
  }, [fetchData, paginationState, selectedFilter, searchText]);

  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(ProjectTableColumns);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Projects" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Projects",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params, event) => {
    navigate(`/projects/${params.row.projectId}`);
  };

  const filterOptions = [
    { label: "All", value: "ALL", icon: <ModeStandbyOutlined /> },
    { label: "To Do", value: "TODO", icon: <InfoOutlined /> },
    { label: "In Progress", value: "INPROGRESS", icon: <InfoOutlined /> },
    // { label: "On Hold", value: "ON_HOLD", icon: <PauseCircleOutline /> },
    { label: "Done", value: "DONE", icon: <CheckCircleOutline /> },
    // { label: "Cancelled", value: "CANCELLED", icon: <CancelOutlined /> },
  ];

  const rows = summaryProjects.map((summaryProject, index) => {
    const client = clients.find((client) => client.id === summaryProject.clientId);
    return {
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      id: index + 1,
      projectId: summaryProject.projectId,
      projectName: summaryProject.projectName,
      customerName: client?.name,
      estimatedAmount: summaryProject.estimatedAmount || 0,
      totalIncome: summaryProject.totalIncome || 0,
      clientBalance: summaryProject.clientBalance,
      totalExpense: summaryProject.totalExpense || 0,
      profit: summaryProject.profit || 0,
    };
  });

  return (
    <CardLayout>
      <MTable
        // Filter data
        selectedFilter={selectedFilter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        showFilterButton={showFilterButton}
        // Search data
        placeholder="Search project"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showSearchBar
        // Export data
        showExportButton={hasExportProject}
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={ProjectTableColumns}
        tableRows={rows}
        rowCount={totalCount || 0}
        loading={loading}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
    </CardLayout>
  );
};
